// AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import usePlatformStats from '../../hooks/usePlatformStats'
import usePortfolioSocietalValue from '../../hooks/portfolio/usePortfolioSocietalValue'
import formatEuro from '../../helpers/formatEuro'
import formatCo2 from '../../helpers/materialsHelpers/formatCo2'
import formatPercentChange from '../../helpers/formatPercentChange'
import formatMonthLabel from '../../helpers/formatMonthLabel'
import materialOutcomeTrend from '../../helpers/dashboard/materialOutcomeTrend'
import { MONITOR_COMPAGNY_ID as DECCOS_MANAGEMENT_TENANT } from '../../constants/monitorTenant'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/**
 * @typedef {object} MaterialOutcomes
 * @property {number|null} Co2Kg
 * @property {number|null} PrivateEur
 * @property {number} HouseholdCount
 * @property {number} UnknownCount
 * @property {Array<{Month: string, Co2Kg: number, PrivateEur: number}>} Series
 */

/** Per kaarttype: welk veld, welke titel en hoe het getal eruitziet. */
const METRICS = {
    'materials-energy-bill': {
        field: 'PrivateEur',
        title: 'Bespaard op energierekening',
        // formatEuro maakt van null € 0,00; een onbekende moet een streepje zijn.
        format: (value) => (value === null || value === undefined ? null : formatEuro(value)),
    },
    'materials-co2': {
        field: 'Co2Kg',
        title: 'Bespaarde CO2',
        format: (value) => (value === null || value === undefined ? null : formatCo2(value)),
    },
}

/**
 * De tegel zelf, los van waar de cijfers vandaan komen.
 *
 * @param {object} props
 * @param {string} props.type - DashboardItems Type ('materials-co2' | 'materials-energy-bill')
 * @param {boolean} props.loading - Staan de gegevens nog onderweg?
 * @param {Error|null} props.error - De ophaalfout, als die er was
 * @param {MaterialOutcomes|null} props.outcomes - null betekent 'geen getal'
 * @param {number} props.missingProjects - Projecten zonder berekening (alleen portfolio)
 */
const MaterialOutcomeTile = ({ type, loading, error, outcomes, missingProjects }) => {
    const history = useHistory()
    const metric = METRICS[type]
    const trend = outcomes ? materialOutcomeTrend(outcomes.Series, metric.field) : null
    const unknown = outcomes?.UnknownCount || 0

    return (
        <div className='participation-value-card participation-value-card--dense'>
            <StaticCardLabel title={metric.title} />

            {error ? (
                <p className='participation-value-error'>We konden de besparingen niet ophalen.</p>
            ) : (
                <>
                    <KpiValue
                        loading={loading}
                        value={metric.format(outcomes?.[metric.field])}
                        unit='per jaar'
                        // Doorklik naar de uitsplitsing per project en per maatregel.
                        onClick={() => history.push(`/${client}/materiaal-besparingen`)}
                        ariaLabel='bekijk per project en per maatregel'
                    />

                    {!loading && trend && (
                        <p className={`participation-value-change ${trend.value > 0 ? 'pos' : trend.value < 0 ? 'neg' : 'neutral'}`}>
                            {formatPercentChange(trend.value)}
                            <span className='participation-value-change-since'>{` sinds ${formatMonthLabel(trend.since)}`}</span>
                        </p>
                    )}

                    {/* Wat er niet in zit, staat erbij: zonder deze regel leest
                        een te laag getal als het hele getal. Eén regel, kort:
                        een KPI-tegel heeft plek voor titel, getal en twee
                        smalle regels.
                        AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero */}
                    {!loading && (missingProjects > 0 || unknown > 0) && (
                        <p className='participation-value-note'>
                            {`Niet meegeteld: ${[
                                missingProjects > 0 && `${missingProjects} ${missingProjects === 1 ? 'project' : 'projecten'}`,
                                unknown > 0 && `${unknown} ${unknown === 1 ? 'registratie' : 'registraties'}`,
                            ].filter(Boolean).join(', ')}`}
                        </p>
                    )}
                </>
            )}
        </div>
    )
}

/**
 * Deccos-beheeromgeving: het platformtotaal uit de nachtrun. Dezelfde listener
 * als de platformtegels, dus geen extra read.
 *
 * @param {object} props
 * @param {string} props.type - DashboardItems Type
 */
const PlatformMaterialOutcomes = ({ type }) => {
    const { stats, loading } = usePlatformStats()
    return (
        <MaterialOutcomeTile
            type={type}
            loading={loading}
            error={null}
            outcomes={stats?.MaterialOutcomes || null}
            missingProjects={0}
        />
    )
}

/**
 * Portfolio: de projecten eronder opgeteld, via de Cloud Function — een
 * portfolio mag de collecties van zijn projecten niet zelf lezen.
 *
 * @param {object} props
 * @param {string} props.type - DashboardItems Type
 */
const PortfolioMaterialOutcomes = ({ type }) => {
    const { data, loading, error } = usePortfolioSocietalValue()
    return (
        <MaterialOutcomeTile
            type={type}
            loading={loading}
            error={error}
            outcomes={data?.materialOutcomes || null}
            missingProjects={data?.materialOutcomesMissing || 0}
        />
    )
}

/**
 * KPI-tegel voor de bespaarde CO2 of de besparing op de energierekening van
 * materialen en coachadviezen, opgeteld over projecten: onder een portfolio
 * (Actienetwerk Energiearmoede) of over het hele platform (Deccos).
 *
 * Nooit opgeteld bij de Maatschappelijke waarde: het geld op de
 * energierekening is van de bewoner, en CO2 heeft een eigen eenheid.
 *
 * @param {object} props
 * @param {{ Type: string }} props.item - DashboardItems doc van deze kaart
 */
const DashboardMaterialOutcomeCard = ({ item }) => {
    const type = item?.Type
    if (!METRICS[type]) return null

    return client === DECCOS_MANAGEMENT_TENANT
        ? <PlatformMaterialOutcomes type={type} />
        : <PortfolioMaterialOutcomes type={type} />
}

export default DashboardMaterialOutcomeCard
